// Reusable numeric validation for Advanced Filter values.
//
// This module is intentionally dependency-free so it can be imported by the
// application AND unit-tested in isolation. It guards against invalid numeric
// values (Infinity, -Infinity, NaN, and non-numeric text) reaching the backend
// query/SQL construction layer — the root cause of errors such as MySQL
// "unknown column 'infinity'".

/**
 * Returns true when a single value (or every value in an array) is a finite
 * number or is blank/absent.
 *
 * A blank/null/undefined value is considered valid because empty filters are
 * simply omitted from the request (they are not numeric inputs). Only values
 * that are present but cannot be represented as a finite number are rejected.
 * Existing valid numeric values are preserved exactly.
 *
 * @param {*} value - a single value or an array of values.
 * @returns {boolean}
 */
function isValidNumericFilterValue(value) {
  if (value === null || value === undefined) return true;

  if (Array.isArray(value)) {
    return value.every(function (item) {
      return isValidNumericFilterValue(item);
    });
  }

  var stringValue = String(value).trim();
  if (stringValue === "") return true;

  // Reject JS-style non-finite tokens explicitly (case-insensitive).
  if (/^[+-]?infinity$/i.test(stringValue) || /^nan$/i.test(stringValue)) {
    return false;
  }

  var numericValue = Number(stringValue);
  return Number.isFinite(numericValue);
}

/**
 * Validates a map of numeric Advanced Filter fields.
 *
 * @param {Object} params - filter params to inspect.
 * @param {string[]} numericKeys - keys within params that must be numeric.
 * @returns {string[]} list of keys that hold invalid numeric values.
 */
function findInvalidNumericFilters(params, numericKeys) {
  if (!params || typeof params !== "object") return [];
  return (numericKeys || []).filter(function (key) {
    return Object.prototype.hasOwnProperty.call(params, key) &&
      !isValidNumericFilterValue(params[key]);
  });
}

export { isValidNumericFilterValue, findInvalidNumericFilters };
